// src/pages/NotificationsInbox.jsx
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  Bell,
  CheckCircle2,
  Info,
  ShieldAlert,
} from "lucide-react";
import PageLayout from "../ui/PageLayout.jsx";
import PageHeader from "../ui/PageHeader.jsx";
import WidgetShell from "../ui/WidgetShell.jsx";
import Button from "../ui/Button.jsx";
import { SmallText } from "../ui/Typography.jsx";
import { useNotifications } from "../contexts/NotificationsContext.jsx";
import { navigateFromNotification } from "../lib/notificationRouting.js";

const FILTERS = [
  { id: "all", label: "All" },
  { id: "unread", label: "Unread" },
  { id: "planner", label: "Planner" },
  { id: "shop", label: "Shop" },
  { id: "kiln", label: "Kiln" },
  { id: "bookings", label: "Bookings" },
  { id: "drive", label: "Drive" },
];

const LEVEL_ICON = {
  info: Info,
  success: CheckCircle2,
  warning: AlertTriangle,
  error: ShieldAlert,
};

function resolveSource(notification) {
  if (!notification) return "system";
  const source = notification.source || "";
  if (source) return source.toLowerCase();
  const type = String(notification.type || "").toLowerCase();
  if (type.includes("shop") || type.includes("offer")) return "shop";
  if (type.includes("booking") || type.includes("workshop")) return "bookings";
  if (type.includes("kiln") || type.includes("firing")) return "kiln";
  if (type.includes("task") || type.includes("planner")) return "planner";
  if (type.includes("drive") || type.includes("sync")) return "drive";
  return "system";
}

function formatTime(ts) {
  if (!ts) return "";
  try {
    return new Date(ts).toLocaleString();
  } catch {
    return "";
  }
}

export default function NotificationsInbox() {
  const navigate = useNavigate();
  const {
    notifications = [],
    markRead,
    markAllRead,
    clearAll,
    removeNotification,
    getUnreadCount,
    notifyAndInbox,
  } = useNotifications() || {};
  const [filter, setFilter] = useState("all");
  const role = useMemo(() => {
    try {
      return localStorage.getItem("mudo:userRole");
    } catch {
      return null;
    }
  }, []);

  const unreadCount = getUnreadCount ? getUnreadCount() : 0;

  const filtered = useMemo(() => {
    const list = Array.isArray(notifications) ? notifications : [];
    const byFilter = list.filter((n) => {
      if (filter === "unread") return n.readAt == null;
      if (filter === "all") return true;
      return resolveSource(n) === filter;
    });
    return byFilter.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
  }, [notifications, filter]);

  const handleSelect = (notification) => {
    if (!notification) return;
    markRead?.(notification.id);
    const targetWorld = notification.world;
    if (targetWorld === "visitor" && role === "studio") {
      notifyAndInbox?.({
        notification: {
          id: `notify-switch-visitor-${notification.id}`,
          title: "Switch to visitor view",
          message: "This notification belongs to the visitor experience.",
          level: "info",
          source: "system",
          world: "studio",
          action: { kind: "NAVIGATE", to: "/discover" },
        },
      });
      navigate("/discover");
      return;
    }
    if (targetWorld === "studio" && role === "visitor") {
      notifyAndInbox?.({
        notification: {
          id: `notify-switch-studio-${notification.id}`,
          title: "Switch to studio view",
          message: "This notification belongs to the studio experience.",
          level: "info",
          source: "system",
          world: "studio",
          action: { kind: "NAVIGATE", to: "/dashboard/home" },
        },
      });
      navigate("/dashboard/home");
      return;
    }
    const result = navigateFromNotification(notification, navigate);
    if (!result?.ok) {
      notifyAndInbox?.({
        notification: {
          id: `notify-missing-${notification.id}`,
          title: "Notification",
          message: "Item no longer exists.",
          level: "warning",
          source: "system",
          world: "studio",
          action: { kind: "NAVIGATE", to: "/dashboard/notifications" },
        },
      });
    }
  };

  return (
    <PageLayout
      header={
        <PageHeader
          title="Notifications"
          subtitle={unreadCount > 0 ? `${unreadCount} unread` : "All caught up"}
          showBack
          actions={
            <div className="flex items-center gap-2">
              <span className="rounded-full border border-[var(--mudo-border)] bg-[var(--mudo-bg-soft)] px-2.5 py-1 text-[11px] text-[var(--mudo-text-muted)]">
                {unreadCount > 0 ? `${unreadCount} unread` : "No unread"}
              </span>
              <Button variant="soft" onClick={() => markAllRead?.()}>
                Mark all read
              </Button>
              <Button variant="soft" onClick={() => clearAll?.()}>
                Clear all
              </Button>
            </div>
          }
        />
      }
    >
      <WidgetShell className="mb-4">
        <div className="flex flex-wrap items-center gap-2">
          {FILTERS.map((item) => (
            <Button
              key={item.id}
              variant={filter === item.id ? "primary" : "soft"}
              className="text-xs px-3 py-1.5 rounded-full"
              onClick={() => setFilter(item.id)}
            >
              {item.label}
            </Button>
          ))}
        </div>
      </WidgetShell>

      <div className="space-y-3">
        {filtered.length === 0 && (
          <WidgetShell className="text-center py-8">
            <SmallText className="text-[var(--mudo-text-muted)]">
              No notifications to show.
            </SmallText>
          </WidgetShell>
        )}

        {filtered.map((notification) => {
          const Icon = LEVEL_ICON[notification.level] || Bell;
          const isUnread = notification.readAt == null;
          const source = resolveSource(notification);
          return (
            <button
              key={notification.id}
              type="button"
              onClick={() => handleSelect(notification)}
              className={`w-full text-left rounded-2xl border px-4 py-3 transition ${
                isUnread
                  ? "border-[var(--mudo-primary)]/50 bg-[var(--mudo-primary-soft)]/30"
                  : "border-[var(--mudo-border)] bg-[var(--mudo-bg-soft)]"
              } hover:border-[var(--mudo-primary)]/60`}
            >
              <div className="flex items-start gap-3">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-[var(--mudo-border)] bg-[var(--mudo-bg-soft)]">
                  <Icon size={18} />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-sm font-semibold text-[var(--mudo-text)] truncate">
                      {notification.title}
                    </div>
                    <span className="text-[11px] text-[var(--mudo-text-muted)]">
                      {formatTime(notification.createdAt)}
                    </span>
                  </div>
                  {notification.message && (
                    <div className="text-sm text-[var(--mudo-text-muted)] mt-0.5">
                      {notification.message}
                    </div>
                  )}
                  <div className="mt-1 text-[10px] uppercase tracking-wide text-[var(--mudo-text-muted)]">
                    {source}
                    {isUnread && <span className="ml-2 text-[var(--mudo-primary)]">Unread</span>}
                  </div>
                </div>
                <div
                  role="button"
                  className="h-8 px-2 text-xs rounded-lg hover:bg-[var(--mudo-bg-soft)] transition-colors flex items-center justify-center cursor-pointer text-[var(--mudo-text-muted)] hover:text-[var(--mudo-text)]"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeNotification?.(notification.id);
                  }}
                >
                  Remove
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </PageLayout>
  );
}
